import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { ErrorCode } from '../../../shared/configs/error-code.config';
import { CustomError } from '../../../shared/models/api';
import { HANDLED_ERRORS } from '../../../shared/utils/http-context';
import { HelperService } from '../../services/helper/helper.service';
import { AuthService } from '../../services/http/auth/auth.service';

import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private helperService: HelperService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip intercepting requests to external auth domain — errors there are handled by auth service directly
    if (request.url.includes(environment.cognito.domain)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(catchError(err => this.handleError(request, next, err)));
  }

  /**
   * On 401, the Cognito access token has most likely expired. Try a silent refresh using the
   * stored refresh_token and replay the original request with the new token. Only if the refresh
   * fails (refresh token expired/revoked) do we fall back to logout. This keeps the session alive
   * the way Keycloak's silent renewal did, instead of logging the user out on every token expiry.
   */
  private handleError(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    httpError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    if (httpError.status === HttpStatusCode.Unauthorized && !(request as any).__retried) {
      return from(this.authService.refreshAccessToken()).pipe(
        switchMap((refreshed: boolean) => {
          if (refreshed) {
            const retried = request.clone({
              setHeaders: { Authorization: `Bearer ${this.authService.accessToken}` },
            });
            (retried as any).__retried = true;
            // Replay once. If it still 401s, refresh genuinely failed → surface as an auth error.
            
            return next
              .handle(retried)
              .pipe(catchError((err: HttpErrorResponse) => this.handleError(retried, next, err)));
          }
          
          return this.notifyAndThrow(request, httpError);
        }),
      );
    }

    return this.notifyAndThrow(request, httpError);
  }

  private notifyAndThrow(request: HttpRequest<unknown>, httpError: HttpErrorResponse) {
    const customError: CustomError = {
      description: httpError.error?.error?.description,
      statusCode: httpError.status,
      statusText: httpError.statusText,
      message:
        typeof httpError.error?.error?.description === 'object'
          ? httpError.error?.error?.description?.[0]?.msg
          : httpError.error?.error?.description,
      errors: httpError.error?.error,
      originalError: httpError,
      errorCode: httpError.error.error?.errorCode,
    };

    // Logout if 401 Unauthorized response returned from api then logout
    if (customError.statusCode === HttpStatusCode.Unauthorized) {
      this.authService.logout();
    }

    // If 403 Forbidden response returned from api then navigate to access denied
    if (customError.statusCode === HttpStatusCode.Forbidden) {
      // access denied logic
    }

    // Show message if error is not handled in component and if not redirected from login
    if (!request.context.get(HANDLED_ERRORS).includes(customError.statusCode)) {
      let message: string;
      switch (customError.statusCode) {
        case HttpStatusCode.Unauthorized:
          message = environment.messages.sessionTimeOut;
          break;

        case HttpStatusCode.InternalServerError:
          message = environment.messages.error;
          break;

        case HttpStatusCode.GatewayTimeout:
          message = environment.messages.gatewayTimeout;
          break;

        default:
          if (customError.errorCode) {
            message = ErrorCode[customError.errorCode as keyof typeof ErrorCode] ?? environment.messages.error;
          } else {
            message = environment.messages.error;
          }

          break;
      }
      this.helperService.showMessage('error', message);
    }

    return throwError(() => customError);
  }
}
